import { minBy } from 'lodash';
import { forkJoin, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { CdkDragEnter } from '@angular/cdk/drag-drop';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  OnDestroy,
  OnInit,
  ChangeDetectorRef,
  HostListener,
  ViewChild,
  ViewChildren,
} from '@angular/core';

import { SnCamera } from '@lib/api/entities';
import { SnCurrentOrganization, SnCurrentUser } from '@lib/auth';
import { SnOrientation, SnScreenService } from '@lib/common/services';
import { SnPlayerGroupReference } from '@lib/player/common';
import { SnVideoPlayerComponent } from '@lib/player/video-player';
import { ITimelineDefinition, TimelineComponent } from '@lib/timeline';
import { t } from '@lib/translation';
import { SnDialogService } from '@lib/ui/dialog';

import { SnCameraGridActionsService } from '../../services/camera-grid-actions.service';
import { SnCameraStreamViewComponent } from '../camera-stream-view';

@Component({
  selector: 'sn-camera-grid',
  templateUrl: './camera-grid.component.html',
  styleUrls: ['./camera-grid.component.scss'],
})
export class SnCameraGridComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild('timelineRef') public timelineRef: TimelineComponent;

  @ViewChildren('stream') public streams: SnCameraStreamViewComponent[];

  @Input()
  set cameras(value: SnCamera[]) {
    this._cameras = value.slice();
    if (this.meta?.axonDevices) {
      this.meta.axonDevices.forEach((device) => {
        const camera = new SnCamera({
          id: device.axonDeviceId,
          isAxonCamera: true,
          axonDevice: device,
        });
        this._cameras.push(camera);
      });
    }
  }

  get displayedCameras(): SnCamera[] {
    return this._displayedCameras;
  }

  get screenOrientation(): SnOrientation {
    return this._screenService.orientation;
  }

  @Input()
  public cols = 3;

  @Input()
  public rows = 3;

  @Input()
  public autoplay = true;

  @Input()
  public dynamic = false;

  @Input()
  public pageCount = 0;

  @Input()
  public page: number = 0;

  @Input()
  public allowTimeout = true;

  @Input()
  public hideBottom: boolean;

  @Input()
  public bulkSearch: boolean = false;

  @Input()
  public meta: any;

  @Output()
  public readonly orderChanged = new EventEmitter<SnCamera[]>();

  @Output()
  public readonly cameraStreamPlay = new EventEmitter<{ live: boolean; camera: SnCamera }>();

  @Output()
  public readonly cameraStreamPause = new EventEmitter<SnCamera>();

  @Output()
  public readonly cameraStreamStop = new EventEmitter<SnCamera>();

  @Output()
  public readonly nextClick = new EventEmitter<Number>();

  @Output()
  public readonly previousClick = new EventEmitter<Number>();

  @Output()
  public readonly dateChange = new EventEmitter<Date>();

  @Output()
  public readonly gridSession = new EventEmitter<boolean>();

  public isPlaying = false;

  public interval: any = null;
  public timelines: ITimelineDefinition[] = [];
  public timelineDef: ITimelineDefinition;
  public camTime: Date;
  public timelineTime: Date;
  public date = new Date();
  public timestampTime: Date;
  public lowestTimePlayer: SnVideoPlayerComponent;
  public timeoutGrid: boolean = false;
  public isSessionActive: boolean = true;
  public minDate = new Date();
  public maxDate = new Date();

  private readonly _destroy$ = new Subject<void>();
  private readonly _cancel$ = new Subject<void>();
  private readonly _removePause$ = new Subject<void>();

  private _cameras: SnCamera[] = [];
  private _displayedCameras: SnCamera[] = [];
  private _pageIndex = 0;
  private _pageSize = 9;
  private _playerGroup: SnPlayerGroupReference;
  private _connectedCams = [];
  private _camsWithFeeds = [];
  private _pausedCams$ = [];
  private _cloudVideoTimeout;

  constructor(
    private readonly _screenService: SnScreenService,
    private readonly _camaraGridService: SnCameraGridActionsService,
    private readonly _cd: ChangeDetectorRef,
    private readonly _currentUser: SnCurrentUser,
    private readonly _currentOrganization: SnCurrentOrganization,
    private readonly _dialogService: SnDialogService,
  ) {
    this._cloudVideoTimeout = this._currentOrganization.settings.cloudVideoTimeout;
  }

  get rewindSpeedFactor(): number {
    return this.lowestTimePlayer?.reference?.rewindSpeedFactor;
  }

  get isFastForwarding(): boolean {
    return this.lowestTimePlayer?.reference?.isFastForwarding;
  }

  get isRewinding(): boolean {
    return this.lowestTimePlayer?.reference?.isRewinding;
  }

  get playbackRate(): number {
    return this.lowestTimePlayer?.reference.playbackRate;
  }

  @HostListener('document:click')
  public documentClick(): void {
    this._camaraGridService.setTimerSlideShow(null);
    clearInterval(this.interval);
  }

  @HostListener('document:click', ['event'])
  @HostListener('document:mousedown', ['$event'])
  @HostListener('document:touchstart', ['$event'])
  @HostListener('document:mouseup', ['$event'])
  @HostListener('document:touchend', ['$event'])
  @HostListener('document:contextmenu', ['$event'])
  @HostListener('window:mousemove', ['$event'])
  @HostListener('window:touchmove', ['$event'])
  @HostListener('window:keydown', ['$event'])
  @HostListener('window:keyup', ['$event'])
  @HostListener('wheel', ['$event'])
  public resetTimer(event: MouseEvent | TouchEvent | WheelEvent): void {
    if (this._currentUser.isVideoWallUser || this.timeoutGrid) {
      return;
    }
    this._camaraGridService.stopTimer();
    this._camaraGridService.startTimer();
  }

  public ngOnChanges(): void {
    this.isPlaying = this.autoplay;
    this._pageSize = this.cols * this.rows;
    this._updateDisplayedCameras();
  }

  public ngOnInit(): void {
    this.date.setHours(0, 0, 0, 0);
    this._camaraGridService.time$.pipe(takeUntil(this._destroy$)).subscribe((res) => {
      clearInterval(this.interval);
      if (res !== 0) {
        this.interval = this._autoSwip(res);
      }
    });
    this._initTimeoutTimer();

    const canChange = this._camaraGridService.canChange;
    const videoWall = this._currentUser.isVideoWallUser;
    if (this.cols > 7 && !canChange && !videoWall && !this.bulkSearch) {
      this.cols = 4;
      this.rows = 4;
    }
    if (this.cols > 3 && this.rows > 3 && this.bulkSearch) {
      this.cols = 3;
      this.rows = 3;
    }
    this._pageSize = this.cols * this.rows;
    this._updateDisplayedCameras();
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public trackCamera(index: number, item: SnCamera): any {
    return item ? item.id : undefined;
  }

  public onPlayerGroupInit(reference: SnPlayerGroupReference): void {
    this._playerGroup = reference;
  }

  public onPlaybackSeek(connected: boolean): void {
    this._connectedCams.push(connected);
    if (this._connectedCams.length === this._camsWithFeeds.length) {
      setTimeout(() => {
        this._cancel$.next();
        const times = [];
        this._playerGroup.players.forEach((player) => {
          const now = Date.now();
          const date = new Date(now - Math.abs(player.currentTime) * 1000);
          times.push({ player, time: date.getTime() });
        });
        const lowestTime = minBy(times, 'time');
        this.lowestTimePlayer = lowestTime.player;
        this.lowestTimePlayer.currentTime$
          .pipe(takeUntil(this._cancel$), takeUntil(this._destroy$))
          .subscribe(() => {
            if (this.timelineDef) {
              if (this.lowestTimePlayer.live) {
                this.timelineTime = new Date();
              } else {
                if (this.timestampTime) {
                  this.timelineTime = this.timestampTime;
                }
              }
              this._cd.detectChanges();
            }
          });
        this.playAll();

        this._cd.detectChanges();
      }, 1000);
    }
  }

  public onDragDropped(): void {
    this.orderChanged.next(this._cameras);
  }

  public onDragEntered(event: CdkDragEnter<number>): void {
    const prevIndex = event.item.data;
    const nextIndex = event.container.data;

    const displayedCamera = this.displayedCameras[prevIndex];
    this.displayedCameras[prevIndex] = this.displayedCameras[nextIndex];
    this.displayedCameras[nextIndex] = displayedCamera;

    const startIndex = this._pageIndex * this._pageSize;
    const camera = this._cameras[prevIndex + startIndex];
    this._cameras[prevIndex + startIndex] = this._cameras[nextIndex + startIndex];
    this._cameras[nextIndex + startIndex] = camera;
  }

  public onCameraStreamPlay(live: boolean, camera: SnCamera): void {
    this.cameraStreamPlay.emit({ live, camera });
  }

  public onCameraStreamPause(camera: SnCamera): void {
    this.cameraStreamPause.emit(camera);
  }

  public onCameraStreamStop(camera: SnCamera): void {
    this.cameraStreamStop.emit(camera);
  }

  public onDateChange(): void {
    this.date = new Date(this.date.getTime());
    this.dateChange.next(this.date);
    this._cd.detectChanges();
  }

  public playAll(): void {
    if (this._playerGroup) {
      this._playerGroup.play();
      this.isPlaying = true;
    }
  }

  public pauseAll(): void {
    if (this._playerGroup) {
      this._playerGroup.pause();
      this.isPlaying = false;
    }
  }

  public forward(amount: number): void {
    if (this._playerGroup) {
      this.pauseAll();
      this._playerGroup.forward(amount);
    }
  }

  public back(amount: number): void {
    if (this._playerGroup) {
      this.pauseAll();
      this._playerGroup.back(amount);
    }
  }
  public fastForward(): void {
    if (this._playerGroup) {
      this._playerGroup.fastForward();
    }
  }
  public rewind(): void {
    if (this._playerGroup) {
      this._playerGroup.rewind();
    }
  }

  public destroyAll(): void {
    if (this._playerGroup) {
      this._playerGroup.destroy(true);
    }
    this._cd.detectChanges();
  }

  public reinitialize(): void {
    if (this._playerGroup) {
      this._playerGroup.initialize();
      this._updateDisplayedCameras();
      this.isSessionActive = true;
      this.timeoutGrid = false;
      this._camaraGridService.startTimer();
      this.gridSession.next(true);
    }
    this._cd.detectChanges();
  }

  public hasPrevPage(): boolean {
    const pageIndex = this.dynamic ? this.page - 1 : this._pageIndex;
    return pageIndex >= 1 && this._pageSize !== 0;
  }

  public hasNextPage(): boolean {
    const numberOfPages = this._getNumberOfPages();
    const page = this.dynamic ? this.page : this._pageIndex;
    return page < numberOfPages && this._pageSize !== 0;
  }

  public nextPage(): void {
    if (!this.hasNextPage()) {
      this._pageIndex = 0;
      this._updateDisplayedCameras();
      return;
    }
    if (this.dynamic) {
      this.nextClick.emit(+this.page + 1);
    } else {
      this._pageIndex++;
      this._updateDisplayedCameras();
      if (this.bulkSearch) {
        this.pauseAll();
      }
    }
  }

  public prevPage(): void {
    if (!this.hasPrevPage()) {
      return;
    }
    if (this.dynamic) {
      this.previousClick.emit(this.page - 1);
    } else {
      this._pageIndex--;
      this._updateDisplayedCameras();
      if (this.bulkSearch) {
        this.pauseAll();
      }
    }
  }

  public hasRecordings(t: ITimelineDefinition | any): Function {
    return (date) => {
      const testDate = new Date(date.getTime());
      const dateStart = testDate.getTime();
      testDate.setDate(testDate.getDate() + 1);
      const dateEnd = testDate.getTime();
      const timeline = t.timeline.filter((stream) => {
        return dateStart < stream.end * 1000 && stream.start * 1000 < dateEnd;
      });
      return timeline.length > 0;
    };
  }

  public onTimeline(timeline: ITimelineDefinition): void {
    this._cd.detectChanges();
    this.timelines.push(timeline);
    const durations = this.timelines.map((t) => t.duration);
    const duration = Math.max(...durations);
    const timelines = this.timelines.map((t) => t.timeline);
    const flattenTimelines = [].concat.apply([], timelines);
    const sortedPeriods = flattenTimelines.sort((a, b) => a.start - b.start);
    this.minDate = new Date(sortedPeriods[0].start * 1000);
    this._cd.detectChanges();
    if (this.timelineDef) {
      this.timelineDef = null;
      this._cd.detectChanges();
    }
    this.timelineDef = {
      duration,
      timeline: sortedPeriods,
    };
    this._cd.detectChanges();

    if (this.timelineRef) {
      this.timelineRef.timeline.redraw();
      this._cd.detectChanges();
    }

    if (this.timelines.length === this._camsWithFeeds.length) {
      this.pauseAll();
    }
    this._cd.detectChanges();
  }

  public moveCursor(date: Date): void {
    if (this._pausedCams$) {
      this._pausedCams$ = [];
      this._removePause$.next();
    }
    this._playerGroup.players.forEach((player) => {
      this._pausedCams$.push(player.pause$);
    });
    forkJoin([this._pausedCams$])
      .pipe(takeUntil(this._removePause$), takeUntil(this._destroy$))
      .subscribe(() => {
        this._connectedCams = [];
        this.camTime = date;
        this.timelineTime = date;
        this._cd.detectChanges();
      });
    this.pauseAll();
    this._cd.detectChanges();
  }

  public onFullscreen(isFullscreen: boolean, camera: SnCamera): void {
    camera.isFullscreen = isFullscreen;
    this._cd.detectChanges();
  }

  private _autoSwip(time: number): any {
    return setInterval(() => {
      this.nextPage();
    }, time);
  }

  private _getNumberOfPages(): number {
    return this.dynamic ? this.pageCount : Math.ceil(this._cameras.length / this._pageSize) - 1;
  }

  private _updateDisplayedCameras(): void {
    if (this.timelines.length) {
      this.timelines = [];
      this._connectedCams = [];
      this.camTime = null;
      this.timelineTime = new Date();
    }
    const startIndex = this._pageIndex * this._pageSize;
    const displayedCameras = this._cameras.slice().splice(startIndex, this._pageSize);

    this._displayedCameras = Array(this._pageSize);
    this._displayedCameras.splice(0, displayedCameras.length, ...displayedCameras);
    this._camsWithFeeds = this._displayedCameras.filter((camera) => !!camera);
    this._cd.detectChanges();
  }

  private _initTimeoutTimer(): void {
    if (this._currentUser.isVideoWallUser || this._currentUser.isSupportUser) {
      return;
    }
    this._camaraGridService
      .initTimeoutTimer()
      .pipe(takeUntil(this._destroy$))
      .subscribe((time) => {
        const now = time * 1000;
        if (now >= this._cloudVideoTimeout * 60000) {
          this.timeoutGrid = true;
          this._camaraGridService.stopTimer();
          if (this._hasCloudStreams()) {
            this._openTimeoutDialog();
          }
        }
      });
  }

  private _openTimeoutDialog(): void {
    this._dialogService
      .timeoutMessage({
        data: {
          message: t('cameras.grid.timeoutMessage', { minutes: this._cloudVideoTimeout }),
        },
      })
      .afterClosed()
      .pipe(takeUntil(this._destroy$))
      .subscribe((confirm) => {
        if (!confirm) {
          this.destroyAll();
          this.isSessionActive = false;
          this.gridSession.next(false);
        } else {
          this.timeoutGrid = false;
          this._camaraGridService.startTimer();
          this.isSessionActive = true;
          this.gridSession.next(true);
        }
      });
  }

  private _hasCloudStreams(): boolean {
    return this.streams.some((c) => !c.localPlaying && c.playStarted);
  }
}
